import { useTranslation } from "react-i18next"
import { Logo } from "@/components/Logo"
import {
  BarChart3,
  Users,
  ArrowRight,
  Tag,
  Workflow,
  FileText,
  Building2,
  Smartphone,
  QrCode,
  Search,
  Plus,
} from "lucide-react"
import type { ComponentType } from "react"
import type { LucideProps } from "lucide-react"

const HAZARD_STRIPE_HEIGHT = 8

const STATUS = {
  cheio:      { bg: "#14181B", ink: "#FFFFFF", label: "CHEIO" },
  vazio:      { bg: "#FFFFFF", ink: "#14181B", label: "VAZIO", border: "#14181B" },
  limpo:      { bg: "#2C7A4D", ink: "#FFFFFF", label: "LIMPO" },
  entregue:   { bg: "#E8541C", ink: "#FFFFFF", label: "ENTREGUE" },
  retornado:  { bg: "#5C5048", ink: "#FFFFFF", label: "RETORNADO" },
  client:     { bg: "#1F4F73", ink: "#FFFFFF", label: "CLIENTE" },
  manutencao: { bg: "#D4A015", ink: "#14181B", label: "MANUTENÇÃO" },
} as const

type StatusKind = keyof typeof STATUS

function HazardStripe({ position }: { position?: "absolute-top" }) {
  return (
    <div
      aria-hidden
      className={`hazard-stripe w-full ${
        position === "absolute-top" ? "absolute inset-x-0 top-0" : ""
      }`}
      style={{ height: HAZARD_STRIPE_HEIGHT }}
    />
  )
}

function NumberedLabel({ n, children }: { n: string; children: React.ReactNode }) {
  return (
    <span className="font-mono text-[11px] uppercase font-semibold tracking-[0.14em] text-foreground inline-flex items-center gap-1.5">
      <span style={{ color: "var(--accent)" }}>{n}</span>
      {children}
    </span>
  )
}

function SectionHeader({ index, label, title, subtitle }: { index: string; label: string; title: string; subtitle?: string }) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center gap-3.5 mb-5">
        <span
          className="font-mono text-[11px] font-bold tracking-[0.18em] uppercase px-2.5 py-1"
          style={{ background: "var(--foreground)", color: "var(--background)" }}
        >
          {index} · {label}
        </span>
        <span aria-hidden className="h-px flex-1" style={{ background: "var(--foreground)" }} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-6 lg:gap-10 items-end">
        <h2
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase text-foreground"
          style={{ letterSpacing: "-0.01em", lineHeight: 1.02 }}
        >
          {title}
        </h2>
        {subtitle && (
          <p className="text-sm sm:text-base text-muted-foreground max-w-xl leading-relaxed pb-1">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  )
}

function StatusChip({ kind }: { kind: StatusKind }) {
  const s = STATUS[kind]
  return (
    <span
      className="inline-flex items-center font-mono whitespace-nowrap"
      style={{
        background: s.bg,
        color: s.ink,
        padding: "2px 7px",
        height: 18,
        borderRadius: 2,
        border: "border" in s ? `1px solid ${s.border}` : `1px solid ${s.bg}`,
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: "0.08em",
      }}
    >
      {s.label}
    </span>
  )
}

function StagePill({ label, color }: { label: string; color: string }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase whitespace-nowrap"
      style={{
        background: color,
        color: "#FFFFFF",
        padding: "4px 10px",
        borderRadius: 3,
        letterSpacing: "0.06em",
      }}
    >
      <span className="w-[5px] h-[5px] rounded-full" style={{ background: "#FFFFFF", opacity: 0.8 }} />
      {label}
    </span>
  )
}

function BrandMark({ inverted = false }: { inverted?: boolean }) {
  const ink = inverted ? "var(--background)" : "var(--foreground)"
  return (
    <div className="flex items-center gap-2.5">
      <Logo variant={inverted ? "light" : "dark"} size={34} />
      <div className="leading-none">
        <div
          className="font-display font-bold text-[16px] uppercase"
          style={{ letterSpacing: "0.04em", color: ink }}
        >
          BierTrack
        </div>
      </div>
    </div>
  )
}

// ---------- HERO RIGHT: Blueprint grid panel ----------

function HeroBlueprint() {
  return (
    <div
      className="relative w-full"
      style={{
        aspectRatio: "1 / 0.78",
        background: "var(--card)",
        border: "1px solid var(--border)",
        backgroundImage:
          "linear-gradient(var(--border-soft) 1px, transparent 1px), linear-gradient(90deg, var(--border-soft) 1px, transparent 1px)",
        backgroundSize: "20px 20px",
      }}
    />
  )
}

// ---------- Mini-screenshot frame (window with title bar + traffic lights) ----------

function ShotFrame({ label, code, height = 360, children }: { label: string; code: string; height?: number; children: React.ReactNode }) {
  return (
    <div
      className="relative"
      style={{
        background: "var(--card)",
        border: "1px solid var(--foreground)",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: "8px 8px 0 0 var(--foreground), 0 30px 60px -30px rgba(20,24,27,0.33)",
      }}
    >
      <div
        className="flex items-center justify-between px-3 py-2"
        style={{ background: "var(--foreground)", color: "var(--background)" }}
      >
        <div className="flex items-center gap-2.5">
          <span className="inline-flex gap-1.5">
            {["#E8541C", "#D4A015", "#2C7A4D"].map((c) => (
              <span key={c} className="w-2 h-2" style={{ background: c, borderRadius: 1 }} />
            ))}
          </span>
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em]">
            {label}
          </span>
        </div>
        <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] opacity-70">
          {code}
        </span>
      </div>
      <div className="relative overflow-hidden" style={{ height }}>{children}</div>
    </div>
  )
}

// ---------- Shot 1: Inventory table ----------

function ShotInventory() {
  const rows: { code: string; type: string; prod: string; cust: string; status: StatusKind; when: string }[] = [
    { code: "BAR-1623", type: "Barril",    prod: "IPA Núcleo",   cust: "Bar do Zé",          status: "entregue",   when: "há 2h" },
    { code: "BAR-1624", type: "Barril",    prod: "IPA Núcleo",   cust: "Depósito Central",   status: "cheio",      when: "há 3h" },
    { code: "CIL-0441", type: "Cilindro",  prod: "CO₂ 6 kg",     cust: "Depósito Central",   status: "cheio",      when: "há 4h" },
    { code: "GEL-0083", type: "Geladeira", prod: "—",            cust: "Empório Vila Mada.", status: "client",     when: "ontem" },
    { code: "BAR-2226", type: "Barril",    prod: "Pale Ale",     cust: "Depósito Central",   status: "limpo",      when: "ontem" },
    { code: "CHP-0112", type: "Chopeira",  prod: "—",            cust: "Boteco 30",          status: "client",     when: "há 2d" },
    { code: "BAR-3013", type: "Barril",    prod: "Stout",        cust: "Em trânsito",        status: "retornado",  when: "há 2d" },
    { code: "BAR-3014", type: "Barril",    prod: "Witbier",      cust: "Lab. QA",            status: "manutencao", when: "há 3d" },
    { code: "BAR-1822", type: "Barril",    prod: "Pilsen",       cust: "Depósito Central",   status: "vazio",      when: "há 4d" },
  ]
  return (
    <div className="h-full flex flex-col" style={{ background: "var(--card)" }}>
      <div
        className="flex items-center gap-2.5 px-3.5 py-2.5"
        style={{ borderBottom: "1px solid var(--border)", background: "var(--background)" }}
      >
        <div
          className="flex-1 h-[26px] flex items-center gap-2 px-2.5 font-mono text-[10.5px] uppercase"
          style={{
            background: "var(--card)",
            border: "1px solid var(--foreground)",
            color: "var(--muted-foreground)",
            letterSpacing: "0.04em",
          }}
        >
          <Search className="w-3 h-3" />
          <span>BUSCAR ATIVO · CÓDIGO, CLIENTE, PRODUTO…</span>
        </div>
        {["TIPO: TODOS", "ETAPA: TODAS", "TAG: —"].map((f) => (
          <span
            key={f}
            className="h-[26px] inline-flex items-center gap-1.5 px-2.5 font-mono text-[10px] uppercase"
            style={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--foreground)",
              letterSpacing: "0.05em",
            }}
          >
            {f} ▾
          </span>
        ))}
        <span
          className="h-[26px] inline-flex items-center gap-1.5 px-3 font-mono text-[10px] font-bold uppercase"
          style={{
            background: "var(--accent)",
            color: "var(--accent-foreground)",
            letterSpacing: "0.1em",
          }}
        >
          <Plus className="w-3 h-3" /> ADICIONAR
        </span>
      </div>
      <div className="flex-1 overflow-hidden">
        <table className="w-full" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["", "Código", "Tipo", "Produto", "Cliente / Local", "Etapa", "Atualizado"].map((h, i) => (
                <th
                  key={i}
                  className="font-mono text-[9.5px] uppercase font-semibold"
                  style={{
                    padding: "9px 10px",
                    textAlign: i === 6 ? "right" : "left",
                    letterSpacing: "0.12em",
                    color: "var(--muted-foreground)",
                    borderBottom: "1px solid var(--foreground)",
                    background: "var(--background)",
                    width: i === 0 ? 28 : undefined,
                  }}
                >
                  {i === 0 ? (
                    <span
                      className="inline-block w-[11px] h-[11px]"
                      style={{ border: "1px solid var(--foreground)" }}
                    />
                  ) : (
                    h
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.code} style={{ background: i % 2 === 0 ? "var(--card)" : "rgba(221,225,229,0.35)" }}>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid var(--border-soft)" }}>
                  <span className="inline-block w-[11px] h-[11px]" style={{ border: "1px solid var(--border)" }} />
                </td>
                <td
                  className="font-mono text-[12px] font-semibold"
                  style={{
                    padding: "9px 10px",
                    color: "var(--foreground)",
                    borderBottom: "1px solid var(--border-soft)",
                    letterSpacing: "0.04em",
                  }}
                >
                  {r.code}
                </td>
                <td className="text-[12px]" style={{ padding: "9px 10px", color: "var(--foreground)", borderBottom: "1px solid var(--border-soft)" }}>{r.type}</td>
                <td className="text-[12px]" style={{ padding: "9px 10px", color: "var(--muted-foreground)", borderBottom: "1px solid var(--border-soft)" }}>{r.prod}</td>
                <td className="text-[12px]" style={{ padding: "9px 10px", color: "var(--foreground)", borderBottom: "1px solid var(--border-soft)" }}>{r.cust}</td>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid var(--border-soft)" }}>
                  <StatusChip kind={r.status} />
                </td>
                <td
                  className="font-mono text-[11px]"
                  style={{
                    padding: "9px 10px",
                    textAlign: "right",
                    color: "var(--muted-foreground)",
                    letterSpacing: "0.04em",
                    borderBottom: "1px solid var(--border-soft)",
                  }}
                >
                  {r.when}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ---------- Shot 2: Asset Detail ----------

function ShotAssetDetail() {
  return (
    <div className="h-full flex" style={{ background: "var(--background)" }}>
      <div
        className="flex flex-col gap-3.5 p-4"
        style={{
          flex: "1.2 1 0%",
          borderRight: "1px solid var(--border)",
          background: "var(--card)",
        }}
      >
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Inventário · Barril
          </div>
          <div className="flex items-center gap-2.5 mt-1.5">
            <h2
              className="font-mono text-[24px] font-bold m-0"
              style={{ color: "var(--foreground)", letterSpacing: "0.04em" }}
            >
              BAR-1623
            </h2>
            <StatusChip kind="entregue" />
          </div>
        </div>

        <div style={{ border: "1px solid var(--border)", background: "var(--background)" }}>
          {[
            ["PRODUTO", "IPA Núcleo · Lote #L-2226"],
            ["CAPACIDADE", "50 L · INOX 304"],
            ["CLIENTE", "Bar do Zé"],
            ["LOCAL", "Vila Madalena · SP"],
            ["TAG", "Centro · Premium"],
          ].map(([k, v], i, arr) => (
            <div
              key={k}
              className="grid gap-2.5 px-2.5 py-2"
              style={{
                gridTemplateColumns: "92px 1fr",
                borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none",
              }}
            >
              <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-muted-foreground pt-px">
                {k}
              </div>
              <div className="text-[12px] font-medium" style={{ color: "var(--foreground)" }}>
                {v}
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-1.5 mt-auto">
          <span
            className="font-mono text-[10px] font-bold uppercase px-2.5 py-1.5"
            style={{
              background: "var(--accent)",
              color: "var(--accent-foreground)",
              letterSpacing: "0.1em",
            }}
          >
            MOVER ETAPA
          </span>
          <span
            className="font-mono text-[10px] font-semibold uppercase px-2.5 py-1.5"
            style={{
              background: "var(--card)",
              color: "var(--foreground)",
              border: "1px solid var(--foreground)",
              letterSpacing: "0.1em",
            }}
          >
            EDITAR
          </span>
          <span
            className="font-mono text-[10px] font-semibold uppercase px-2.5 py-1.5"
            style={{
              background: "var(--card)",
              color: "var(--foreground)",
              border: "1px solid var(--border)",
              letterSpacing: "0.1em",
            }}
          >
            QR
          </span>
        </div>
      </div>

      <div className="flex-1 p-4 flex flex-col gap-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
          Histórico de eventos
        </div>
        {[
          { from: "cheio" as StatusKind,    to: "entregue" as StatusKind,   who: "Carla M.",     when: "HÁ 2H",  note: "Bar do Zé · NF 8842" },
          { from: "limpo" as StatusKind,    to: "cheio" as StatusKind,      who: "Linha A",      when: "ONTEM",  note: "IPA Núcleo · 50 L" },
          { from: "vazio" as StatusKind,    to: "limpo" as StatusKind,      who: "Higienização", when: "3 DIAS", note: "Ciclo CIP #4422" },
          { from: "entregue" as StatusKind, to: "retornado" as StatusKind,  who: "Frota 03",     when: "5 DIAS", note: "Devolução vazio" },
        ].map((e, i) => (
          <div
            key={i}
            className="px-2.5 py-2 flex flex-col gap-1.5"
            style={{ border: "1px solid var(--border)", background: "var(--card)" }}
          >
            <div className="flex items-center gap-1.5">
              <StatusChip kind={e.from} />
              <span className="font-mono text-[11px] text-muted-foreground">→</span>
              <StatusChip kind={e.to} />
              <span className="flex-1" />
              <span className="font-mono text-[9.5px] tracking-[0.08em] text-muted-foreground">
                {e.when}
              </span>
            </div>
            <div className="flex justify-between font-mono text-[10.5px] tracking-[0.04em]">
              <span className="text-muted-foreground">{e.who}</span>
              <span style={{ color: "var(--foreground)" }}>{e.note}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------- Shot 3: Dashboard with donut + bar chart ----------

function ShotDashboard() {
  const stats: { l: string; v: number; tone: string }[] = [
    { l: "TOTAL",     v: 100, tone: "var(--foreground)" },
    { l: "ENTREGUE",  v: 26,  tone: "#E8541C" },
    { l: "CHEIO",     v: 19,  tone: "#14181B" },
    { l: "RETORNADO", v: 22,  tone: "#5C5048" },
    { l: "LIMPO",     v: 12,  tone: "#2C7A4D" },
    { l: "VAZIO",     v: 18,  tone: "#FFFFFF" },
  ]

  const donut: { name: string; v: number; c: string; border?: string }[] = [
    { name: "Entregue",  v: 26, c: "#E8541C" },
    { name: "Retornado", v: 22, c: "#5C5048" },
    { name: "Cheio",     v: 19, c: "#14181B" },
    { name: "Vazio",     v: 18, c: "#FFFFFF", border: "#14181B" },
    { name: "Limpo",     v: 12, c: "#2C7A4D" },
    { name: "Client",    v: 3,  c: "#1F4F73" },
  ]
  const total = donut.reduce((s, x) => s + x.v, 0)

  const arc = (sf: number, ef: number, r1: number, r2: number) => {
    const a1 = sf * 2 * Math.PI - Math.PI / 2
    const a2 = ef * 2 * Math.PI - Math.PI / 2
    const large = ef - sf > 0.5 ? 1 : 0
    const cx = 150, cy = 150
    const x1 = cx + r1 * Math.cos(a1), y1 = cy + r1 * Math.sin(a1)
    const x2 = cx + r1 * Math.cos(a2), y2 = cy + r1 * Math.sin(a2)
    const x3 = cx + r2 * Math.cos(a2), y3 = cy + r2 * Math.sin(a2)
    const x4 = cx + r2 * Math.cos(a1), y4 = cy + r2 * Math.sin(a1)
    return `M ${x1} ${y1} A ${r1} ${r1} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${r2} ${r2} 0 ${large} 0 ${x4} ${y4} Z`
  }

  let cum = 0
  const segs = donut.map((x) => {
    const start = cum / total
    cum += x.v
    const end = cum / total
    return { ...x, start, end }
  })

  const bars = [
    { n: "Pale Ale", v: 23 },
    { n: "IPA",      v: 19 },
    { n: "Witbier",  v: 16 },
    { n: "Stout",    v: 13 },
    { n: "Pilsen",   v: 10 },
  ]
  const maxBar = 24

  return (
    <div
      className="h-full p-4 flex flex-col gap-3"
      style={{ background: "var(--background)" }}
    >
      <div className="flex justify-between items-center">
        <div>
          <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-muted-foreground">
            VISÃO GERAL · ATUALIZADO 14:22
          </div>
          <div
            className="font-display text-[18px] font-bold mt-0.5"
            style={{ color: "var(--foreground)", letterSpacing: "-0.01em" }}
          >
            Boa tarde, Lucas.
          </div>
        </div>
        <span
          className="font-mono text-[10px] font-bold uppercase px-2.5 py-1.5"
          style={{
            background: "var(--accent)",
            color: "var(--accent-foreground)",
            letterSpacing: "0.1em",
          }}
        >
          + ADICIONAR ATIVO
        </span>
      </div>

      <div className="grid grid-cols-6 gap-1.5">
        {stats.map((s) => (
          <div
            key={s.l}
            className="relative overflow-hidden px-2.5 py-2"
            style={{ background: "var(--card)", border: "1px solid var(--border)" }}
          >
            <div
              className="absolute left-0 top-0 bottom-0"
              style={{ width: 3, background: s.tone, borderLeft: s.tone === "#FFFFFF" ? "1px solid var(--foreground)" : "none" }}
            />
            <div className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-muted-foreground pl-1.5">
              {s.l}
            </div>
            <div
              className="font-display text-[22px] font-bold leading-tight pl-1.5"
              style={{ color: "var(--foreground)" }}
            >
              {s.v}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2.5 flex-1 min-h-0">
        {/* Donut */}
        <div className="p-3 flex flex-col" style={{ background: "var(--card)", border: "1px solid var(--border)" }}>
          <div className="flex justify-between items-baseline mb-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
              Ativos por etapa
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.06em] text-muted-foreground">
              DIST.
            </span>
          </div>
          <div className="grid items-center flex-1" style={{ gridTemplateColumns: "auto 1fr", gap: 12 }}>
            <svg viewBox="0 0 300 300" width="138" height="138" className="block">
              {segs.map((s, i) => (
                <path
                  key={i}
                  d={arc(s.start, s.end, 110, 70)}
                  fill={s.c}
                  stroke={s.border || "var(--card)"}
                  strokeWidth={s.border ? 1.5 : 2}
                />
              ))}
              <text
                x="150" y="142" textAnchor="middle"
                fontFamily="var(--font-mono)" fontSize="14"
                letterSpacing="3" fill="var(--muted-foreground)"
                fontWeight="600"
              >
                TOTAL
              </text>
              <text
                x="150" y="186" textAnchor="middle"
                fontFamily="var(--font-display)" fontSize="56"
                fontWeight="700" fill="var(--foreground)"
                letterSpacing="-2"
              >
                {total}
              </text>
            </svg>
            <div className="grid grid-cols-2 gap-1">
              {donut.map((x) => (
                <div key={x.name} className="flex items-center gap-1.5 px-1 py-0.5">
                  <span
                    className="flex-shrink-0 w-2.5 h-2.5"
                    style={{
                      background: x.c,
                      border: x.border ? `1px solid ${x.border}` : "none",
                    }}
                  />
                  <span className="text-[10.5px] flex-1" style={{ color: "var(--foreground)" }}>
                    {x.name}
                  </span>
                  <span
                    className="font-mono text-[10.5px] font-bold"
                    style={{ color: "var(--foreground)" }}
                  >
                    {x.v}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top products bar chart */}
        <div className="p-3 flex flex-col gap-1.5" style={{ background: "var(--card)", border: "1px solid var(--border)" }}>
          <div className="flex justify-between items-baseline">
            <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
              Maiores produtos · barris
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.06em] text-muted-foreground">
              UN.
            </span>
          </div>
          {bars.map((b, i) => (
            <div
              key={b.n}
              className="grid items-center gap-2"
              style={{ gridTemplateColumns: "60px 1fr 24px" }}
            >
              <span className="text-[11px] font-medium" style={{ color: "var(--foreground)" }}>
                {b.n}
              </span>
              <div className="h-3.5 relative" style={{ background: "var(--border-soft)" }}>
                <div
                  className="absolute inset-y-0 left-0"
                  style={{
                    width: `${(b.v / maxBar) * 100}%`,
                    background: i === 0 ? "var(--accent)" : "var(--foreground)",
                    backgroundImage:
                      i === 0
                        ? "repeating-linear-gradient(45deg, transparent 0 4px, rgba(255,255,255,0.18) 4px 6px)"
                        : undefined,
                  }}
                />
              </div>
              <span
                className="font-mono text-[11px] font-semibold text-right"
                style={{ color: "var(--foreground)" }}
              >
                {b.v}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ---------- Shot 4: Scan phone + ledger ----------

function ShotScan() {
  return (
    <div
      className="h-full p-4 grid items-stretch"
      style={{ gridTemplateColumns: "190px 1fr", gap: 18, background: "var(--background)" }}
    >
      {/* Phone frame */}
      <div className="grid place-items-center relative">
        <div
          className="relative p-1.5"
          style={{
            width: 178,
            height: 348,
            background: "var(--foreground)",
            borderRadius: 22,
            boxShadow: "0 30px 50px -25px rgba(20,24,27,0.6)",
          }}
        >
          <div
            className="w-full h-full overflow-hidden flex flex-col relative"
            style={{ background: "var(--foreground)", borderRadius: 16 }}
          >
            {/* status bar */}
            <div
              className="flex justify-between font-mono font-semibold"
              style={{
                padding: "5px 14px 4px",
                fontSize: 8.5,
                letterSpacing: "0.06em",
                color: "var(--background)",
              }}
            >
              <span>14:22</span>
              <span
                className="inline-block"
                style={{ width: 14, height: 6, border: "1px solid var(--background)", borderRadius: 1, position: "relative" }}
              >
                <span
                  className="absolute"
                  style={{ inset: 1, right: "30%", background: "var(--background)" }}
                />
              </span>
            </div>
            {/* app header */}
            <div
              className="flex justify-between items-center"
              style={{
                padding: "4px 12px 8px",
                borderBottom: "1px solid rgba(237,238,238,0.1)",
              }}
            >
              <span
                className="font-mono font-bold uppercase"
                style={{ fontSize: 9, letterSpacing: "0.16em", color: "var(--background)" }}
              >
                ◀ ESCANEAR
              </span>
              <span
                className="font-mono font-bold uppercase"
                style={{ fontSize: 8.5, letterSpacing: "0.12em", color: "var(--accent)" }}
              >
                ● AO VIVO
              </span>
            </div>
            {/* viewfinder */}
            <div
              className="relative overflow-hidden"
              style={{ margin: "12px 14px 10px", height: 188, background: "#0c1014", borderRadius: 6 }}
            >
              <div
                className="absolute inset-0"
                style={{
                  background: "radial-gradient(ellipse at center, transparent 30%, rgba(20,24,27,0.8) 100%)",
                }}
              />
              {([
                { t: 8, l: 8, br: ["t", "l"] },
                { t: 8, r: 8, br: ["t", "r"] },
                { b: 8, l: 8, br: ["b", "l"] },
                { b: 8, r: 8, br: ["b", "r"] },
              ] as { t?: number; b?: number; l?: number; r?: number; br: string[] }[]).map((c, i) => (
                <div
                  key={i}
                  className="absolute"
                  style={{
                    width: 22, height: 22,
                    top: c.t,
                    bottom: c.b,
                    left: c.l,
                    right: c.r,
                    borderTop: c.br.includes("t") ? "2.5px solid var(--accent)" : undefined,
                    borderBottom: c.br.includes("b") ? "2.5px solid var(--accent)" : undefined,
                    borderLeft: c.br.includes("l") ? "2.5px solid var(--accent)" : undefined,
                    borderRight: c.br.includes("r") ? "2.5px solid var(--accent)" : undefined,
                  }}
                />
              ))}
              <div className="absolute inset-0 grid place-items-center">
                <svg width="80" height="80" viewBox="0 0 80 80">
                  {[[6, 6], [58, 6], [6, 58]].map(([x, y], i) => (
                    <g key={i}>
                      <rect x={x} y={y} width="16" height="16" stroke="#EDEEEE" strokeWidth="1.6" fill="none" />
                      <rect x={x + 5} y={y + 5} width="6" height="6" fill="#EDEEEE" />
                    </g>
                  ))}
                  {[
                    [30, 10, 3], [36, 14, 2], [40, 8, 2], [46, 14, 3],
                    [12, 30, 3], [20, 32, 2], [28, 30, 3], [36, 34, 2], [44, 30, 3], [52, 32, 2], [60, 30, 3],
                    [16, 40, 2], [24, 44, 3], [32, 42, 2], [40, 46, 3], [50, 40, 2], [58, 44, 3],
                    [30, 52, 3], [40, 56, 2], [48, 52, 3], [56, 56, 2],
                    [30, 64, 2], [38, 68, 3], [46, 64, 2], [54, 68, 3], [62, 66, 2],
                  ].map(([x, y, sz], i) => (
                    <rect key={i} x={x} y={y} width={sz} height={sz} fill="#EDEEEE" />
                  ))}
                </svg>
              </div>
              <div
                className="absolute"
                style={{
                  left: 8, right: 8, top: "50%", height: 2,
                  background: "var(--accent)",
                  boxShadow: "0 0 14px var(--accent), 0 0 4px var(--accent)",
                  animation: "bf-scan-line 2.4s ease-in-out infinite",
                }}
              />
            </div>
            {/* result strip */}
            <div
              className="flex flex-col gap-0.5"
              style={{
                margin: "0 14px",
                padding: "8px 10px",
                background: "var(--background)",
                borderLeft: "3px solid var(--accent)",
              }}
            >
              <div className="flex justify-between items-center">
                <span
                  className="font-mono font-bold uppercase"
                  style={{ fontSize: 8, letterSpacing: "0.16em", color: "var(--accent)" }}
                >
                  ✓ DETECTADO
                </span>
                <span
                  className="font-mono uppercase"
                  style={{ fontSize: 8, letterSpacing: "0.1em", color: "var(--muted-foreground)" }}
                >
                  14:22:09
                </span>
              </div>
              <div
                className="font-mono font-bold"
                style={{ fontSize: 13, letterSpacing: "0.04em", color: "var(--foreground)" }}
              >
                BAR-1623
              </div>
              <div className="text-[9.5px] leading-tight" style={{ color: "var(--muted-foreground)" }}>
                IPA Núcleo · 50 L · Bar do Zé
              </div>
            </div>
            {/* etapa transition */}
            <div
              className="flex items-center gap-1.5 justify-center"
              style={{
                margin: "8px 14px 0",
                padding: "5px 8px",
                border: "1px solid rgba(237,238,238,0.13)",
              }}
            >
              <StatusChip kind="cheio" />
              <span className="font-mono text-[10px]" style={{ color: "rgba(237,238,238,0.5)" }}>→</span>
              <StatusChip kind="entregue" />
            </div>
            {/* CTA */}
            <div
              className="text-center font-mono font-bold uppercase mt-auto"
              style={{
                margin: "auto 14px 14px",
                padding: "9px 0",
                background: "var(--accent)",
                color: "var(--accent-foreground)",
                fontSize: 10.5,
                letterSpacing: "0.16em",
              }}
            >
              CONFIRMAR MOVIMENTO
            </div>
          </div>
        </div>
      </div>

      {/* Right: session ledger */}
      <div className="flex flex-col gap-1.5 min-w-0">
        <div className="flex justify-between items-baseline">
          <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-muted-foreground">
            Sessão · 14:22
          </span>
          <span
            className="font-mono text-[9.5px] uppercase font-bold"
            style={{ color: "var(--foreground)", letterSpacing: "0.1em" }}
          >
            9 LEITURAS
          </span>
        </div>
        <div className="flex-1 flex flex-col" style={{ border: "1px solid var(--foreground)", background: "var(--card)" }}>
          <div
            className="grid gap-2 px-2 py-1.5 font-mono uppercase font-semibold"
            style={{
              gridTemplateColumns: "78px 1fr 36px",
              fontSize: 8.5,
              letterSpacing: "0.14em",
              color: "var(--muted-foreground)",
              background: "var(--background)",
              borderBottom: "1px solid var(--foreground)",
            }}
          >
            <span>CÓDIGO</span><span>MOVIMENTO</span>
            <span className="text-right">HORA</span>
          </div>
          {([
            { c: "BAR-1623", from: "cheio",    to: "entregue",   t: "14:22" },
            { c: "BAR-1624", from: "cheio",    to: "entregue",   t: "14:21" },
            { c: "CIL-0441", from: "vazio",    to: "cheio",      t: "14:18" },
            { c: "BAR-2226", from: "vazio",    to: "limpo",      t: "14:14" },
            { c: "BAR-3013", from: "entregue", to: "retornado",  t: "14:09" },
            { c: "BAR-3014", from: "limpo",    to: "manutencao", t: "14:04" },
            { c: "GEL-0083", from: "vazio",    to: "client",     t: "14:01" },
          ] as { c: string; from: StatusKind; to: StatusKind; t: string }[]).map((s, i, arr) => (
            <div
              key={s.c}
              className="grid items-center gap-2 px-2 py-1.5"
              style={{
                gridTemplateColumns: "78px 1fr 36px",
                background: i % 2 === 0 ? "var(--card)" : "rgba(221,225,229,0.35)",
                borderBottom: i < arr.length - 1 ? "1px solid var(--border-soft)" : "none",
              }}
            >
              <span
                className="font-mono text-[11px] font-bold"
                style={{ color: "var(--foreground)", letterSpacing: "0.04em" }}
              >
                {s.c}
              </span>
              <div className="flex items-center gap-1 flex-wrap">
                <StatusChip kind={s.from} />
                <span className="font-mono text-[10px] text-muted-foreground">→</span>
                <StatusChip kind={s.to} />
              </div>
              <span
                className="font-mono text-[9.5px] text-right"
                style={{ color: "var(--muted-foreground)", letterSpacing: "0.06em" }}
              >
                {s.t}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ---------- Capability tile with hover stamp shadow ----------

interface CapItem {
  icon: ComponentType<LucideProps>
  title: string
  description: string
}

function CapabilityTile({ index, item }: { index: number; item: CapItem }) {
  const Icon = item.icon
  const num = String(index + 1).padStart(2, "0")
  return (
    <div
      className="relative p-5 transition-[transform,box-shadow,border-color] duration-150 ease-out hover:-translate-x-0.5 hover:-translate-y-0.5"
      style={{
        background: "var(--background)",
        border: "1px solid var(--border)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--foreground)"
        e.currentTarget.style.boxShadow = "4px 4px 0 0 var(--accent)"
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--border)"
        e.currentTarget.style.boxShadow = "none"
      }}
    >
      <div className="flex justify-between items-start mb-3.5">
        <span
          className="font-mono text-[10px] font-bold tracking-[0.18em]"
          style={{ color: "var(--accent)" }}
        >
          C-{num}
        </span>
        <span
          className="grid place-items-center w-6 h-6 font-mono text-[10px] font-bold"
          style={{ border: "1px solid var(--foreground)", color: "var(--foreground)" }}
        >
          +
        </span>
      </div>
      <Icon className="h-5 w-5 mb-2.5" style={{ color: "var(--foreground)" }} />
      <h3
        className="font-display font-bold uppercase mb-2"
        style={{ fontSize: 16, letterSpacing: "0.02em", color: "var(--foreground)" }}
      >
        {item.title}
      </h3>
      <p className="text-[13px] leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
        {item.description}
      </p>
    </div>
  )
}

// ---------- Page ----------

export default function Home() {
  const { t } = useTranslation()

  const howItWorksSteps = [
    { code: "REG",   icon: Tag,         title: t("home.howItWorks.step1.title"), description: t("home.howItWorks.step1.description") },
    { code: "SCAN",  icon: QrCode,      title: t("home.howItWorks.step2.title"), description: t("home.howItWorks.step2.description") },
    { code: "MOVE",  icon: Workflow,    title: t("home.howItWorks.step3.title"), description: t("home.howItWorks.step3.description") },
    { code: "AUDIT", icon: BarChart3,   title: t("home.howItWorks.step4.title"), description: t("home.howItWorks.step4.description") },
  ]

  const capabilities: CapItem[] = [
    { icon: Users,      title: t("home.capabilities.permissions.title"), description: t("home.capabilities.permissions.description") },
    { icon: FileText,   title: t("home.capabilities.contracts.title"),   description: t("home.capabilities.contracts.description") },
    { icon: Tag,        title: t("home.capabilities.tags.title"),        description: t("home.capabilities.tags.description") },
    { icon: Building2,  title: t("home.capabilities.contacts.title"),    description: t("home.capabilities.contacts.description") },
    { icon: BarChart3,  title: t("home.capabilities.analytics.title"),   description: t("home.capabilities.analytics.description") },
    { icon: Smartphone, title: t("home.capabilities.mobile.title"),      description: t("home.capabilities.mobile.description") },
  ]

  const heroAssetLegend: { code: string; label: string; n: number }[] = [
    { code: "BAR", label: t("home.hero.assetKegs"),    n: 100 },
    { code: "GEL", label: t("home.hero.assetFridges"), n: 14  },
    { code: "CIL", label: t("home.hero.assetCO2"),     n: 22  },
    { code: "CHP", label: t("home.hero.assetTaps"),    n: 8   },
  ]

  return (
    <div className="min-h-screen text-foreground" style={{ background: "var(--background)" }}>
      <HazardStripe />

      {/* NAV */}
      <nav
        className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-background/85"
        style={{ background: "var(--background)", borderBottom: "1px solid var(--foreground)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <BrandMark />
            </div>
            <div className="hidden md:flex items-center gap-6">
              <NumberedLabel n="01">Sistema</NumberedLabel>
              <NumberedLabel n="02">Operações</NumberedLabel>
              <NumberedLabel n="03">Capacidades</NumberedLabel>
              <span aria-hidden className="w-px h-5" style={{ background: "var(--foreground)" }} />
              <a
                href="/login"
                className="font-mono text-[11px] uppercase font-semibold tracking-[0.14em] hover:opacity-70 transition-opacity"
              >
                {t("home.signIn")}
              </a>
              <a
                href="/register"
                className="inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase px-3.5 py-2 transition-opacity hover:opacity-90"
                style={{
                  background: "var(--accent)",
                  color: "var(--accent-foreground)",
                  letterSpacing: "0.14em",
                }}
              >
                {t("home.getStarted")} →
              </a>
            </div>
            <a
              href="/register"
              className="md:hidden font-mono text-[11px] font-bold uppercase px-3 py-1.5"
              style={{
                background: "var(--accent)",
                color: "var(--accent-foreground)",
                letterSpacing: "0.14em",
              }}
            >
              {t("home.getStarted")}
            </a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{
          padding: "64px 16px 72px",
          background: "var(--background)",
          borderBottom: "1px solid var(--foreground)",
        }}
      >
        {/* Corner crosshair marks */}
        {[
          { top: 24, left: 24 },
          { top: 24, right: 24 },
          { bottom: 24, left: 24 },
          { bottom: 24, right: 24 },
        ].map((p, i) => (
          <div key={i} className="absolute pointer-events-none" style={{ ...p, width: 14, height: 14, opacity: 0.7 }}>
            <div className="absolute" style={{ left: 6, top: 0, bottom: 0, width: 1, background: "var(--foreground)" }} />
            <div className="absolute" style={{ top: 6, left: 0, right: 0, height: 1, background: "var(--foreground)" }} />
          </div>
        ))}

        <div
          className="max-w-7xl mx-auto grid gap-10 lg:gap-14 items-center relative px-4 sm:px-6 lg:px-8"
          style={{ gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 0.95fr)" }}
        >
          <div className="min-w-0">
            <div
              className="inline-flex items-center gap-2.5 px-2.5 py-1.5 font-mono font-bold uppercase"
              style={{
                background: "var(--foreground)",
                color: "var(--background)",
                fontSize: 10,
                letterSpacing: "0.16em",
              }}
            >
              <span className="w-1.5 h-1.5" style={{ background: "var(--accent)" }} />
              {t("home.hero.badge", { defaultValue: "GESTÃO DE ATIVOS · CERVEJARIAS" })}
            </div>
            <h1
              className="font-display font-bold uppercase mt-5"
              style={{
                fontSize: "clamp(44px, 7.5vw, 84px)",
                lineHeight: 0.94,
                letterSpacing: "-0.025em",
                color: "var(--foreground)",
              }}
            >
              CADA BARRIL.<br />
              CADA ATIVO.<br />
              <span className="relative inline-block">
                <span
                  aria-hidden
                  className="absolute"
                  style={{
                    inset: "0.04em -0.05em 0.04em -0.05em",
                    background: "var(--accent)",
                    zIndex: 0,
                  }}
                />
                <span className="relative" style={{ color: "var(--background)", padding: "0 0.05em" }}>
                  RASTREADO
                </span>
              </span>
            </h1>
            <p
              className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed"
              style={{ color: "var(--muted-foreground)" }}
            >
              {t("home.hero.subtitle")}
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              <a
                href="/register"
                className="inline-flex items-center gap-2 font-mono font-bold uppercase px-5 py-3 transition-opacity hover:opacity-90"
                style={{
                  background: "var(--accent)",
                  color: "var(--accent-foreground)",
                  fontSize: 12,
                  letterSpacing: "0.16em",
                }}
              >
                COMEÇAR · GRÁTIS
              </a>
              <a
                href="/login"
                className="inline-flex items-center gap-2 font-mono font-bold uppercase px-5 py-3 transition-colors hover:bg-secondary"
                style={{
                  background: "transparent",
                  color: "var(--foreground)",
                  border: "1px solid var(--foreground)",
                  fontSize: 12,
                  letterSpacing: "0.16em",
                }}
              >
                VER DEMO →
              </a>
            </div>

            {/* Asset legend */}
            <div
              className="mt-9 pt-4 grid grid-cols-2 sm:grid-cols-4 gap-0"
              style={{ borderTop: "1px solid var(--foreground)" }}
            >
              {heroAssetLegend.map((a, i, arr) => (
                <div
                  key={a.code}
                  className="px-3 py-2.5"
                  style={{
                    borderRight: i < arr.length - 1 ? "1px solid var(--border)" : "none",
                  }}
                >
                  <div
                    className="font-mono text-[9.5px] font-bold tracking-[0.16em]"
                    style={{ color: "var(--accent)" }}
                  >
                    /{a.code}
                  </div>
                  <div
                    className="font-display text-[22px] font-bold leading-none mt-0.5"
                    style={{ color: "var(--foreground)", letterSpacing: "-0.015em" }}
                  >
                    {a.n}
                  </div>
                  <div
                    className="font-mono text-[9px] uppercase mt-0.5"
                    style={{ color: "var(--muted-foreground)", letterSpacing: "0.14em" }}
                  >
                    {a.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:block min-w-0">
            <HeroBlueprint />
          </div>
        </div>
      </section>

      {/* TICKER — assets streaming left → right */}
      <section className="overflow-hidden" style={{ background: "var(--foreground)", color: "var(--background)" }}>
        <div
          className="flex items-center whitespace-nowrap py-3 font-mono text-[11px] tracking-[0.14em]"
          style={{
            width: "max-content",
            animation: "bf-ticker 60s linear infinite",
            willChange: "transform",
          }}
        >
          {Array.from({ length: 2 }).flatMap((_, k) =>
            [
              { c: "BAR-1623", s: "ENTREGUE",   cu: "BAR DO ZÉ" },
              { c: "CIL-0441", s: "CHEIO",      cu: "DEPÓSITO CENTRAL" },
              { c: "GEL-0083", s: "ENTREGUE",   cu: "EMPÓRIO V. MADA." },
              { c: "BAR-2226", s: "LIMPO",      cu: "DEPÓSITO CENTRAL" },
              { c: "CHP-0112", s: "ENTREGUE",   cu: "BOTECO 30" },
              { c: "BAR-3013", s: "RETORNADO",  cu: "EM TRÂNSITO" },
              { c: "BAR-3014", s: "MANUTENÇÃO", cu: "LAB. QA" },
            ].map((r, i) => (
              <span key={`${k}-${i}`} className="inline-flex items-center gap-2.5 pr-9">
                <span style={{ color: "var(--accent)" }}>■</span>
                <span className="font-bold">{r.c}</span>
                <span className="opacity-40">·</span>
                <span style={{ color: "var(--accent)" }} className="font-semibold">{r.s}</span>
                <span className="opacity-40">→</span>
                <span className="opacity-75">{r.cu}</span>
              </span>
            ))
          )}
        </div>
      </section>

      {/* SECTION 01 — O Sistema (embedded screenshots) */}
      <section
        className="py-20 sm:py-24 lg:py-28"
        style={{ background: "var(--card)", borderBottom: "1px solid var(--border)" }}
      >
        <SectionHeader
          index="01"
          label="O SISTEMA"
          title="O CHÃO DE FÁBRICA, EM UMA TELA."
          subtitle="Quatro telas. Um único fluxo. Cada barril, geladeira ou cilindro entra no sistema, ganha um QR e nunca mais some entre planilhas."
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 grid grid-cols-1 lg:grid-cols-2 gap-7 lg:gap-10">
          {[
            { num: "01", title: "Inventário",          caption: "Tabela mestre · 100 ativos rastreados · 6 etapas operacionais.",                code: "/inventory",     label: "INVENTÁRIO · BARRIS",   shot: <ShotInventory /> },
            { num: "02", title: "Detalhe do Ativo",    caption: "Cada código carrega especificação, posição e histórico completo.",               code: "/asset/1623",    label: "ATIVO · BAR-1623",      shot: <ShotAssetDetail /> },
            { num: "03", title: "Painel",              caption: "Distribuição por etapa, maiores produtos, leituras do dia. Atualizado a cada movimento.", code: "/dashboard",     label: "PAINEL",                shot: <ShotDashboard /> },
            { num: "04", title: "Escanear",            caption: "Câmera do celular vira leitor industrial. Sessões em lote, sincronização offline.",     code: "/scan",          label: "CELULAR · LEITOR",      shot: <ShotScan /> },
          ].map((p) => (
            <div key={p.num} className="flex flex-col gap-3.5 min-w-0">
              <div className="flex items-baseline gap-2.5">
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--accent)" }}>
                  TELA {p.num}
                </span>
                <span aria-hidden className="flex-1 h-px" style={{ background: "var(--border)" }} />
                <span
                  className="font-display text-[18px] font-bold uppercase"
                  style={{ color: "var(--foreground)", letterSpacing: "0.02em" }}
                >
                  {p.title}
                </span>
              </div>
              <ShotFrame label={p.label} code={p.code} height={420}>
                {p.shot}
              </ShotFrame>
              <p className="text-[13px] leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
                {p.caption}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 02 — Operações */}
      <section className="relative py-20 sm:py-24 lg:py-28">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: 0.3,
            backgroundImage:
              "linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
            maskImage:
              "linear-gradient(180deg, transparent 0%, black 30%, black 70%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(180deg, transparent 0%, black 30%, black 70%, transparent 100%)",
          }}
        />
        <div className="relative">
          <SectionHeader
            index="02"
            label="OPERAÇÕES"
            title={t("home.howItWorks.title")}
            subtitle={t("home.howItWorks.subtitle")}
          />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
              style={{ border: "1px solid var(--foreground)", background: "var(--card)" }}
            >
              {howItWorksSteps.map((step, i, arr) => {
                const Icon = step.icon
                return (
                  <div
                    key={step.title}
                    className="relative p-6"
                    style={{
                      borderRight: i < arr.length - 1 ? "1px solid var(--border)" : "none",
                      borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none",
                    }}
                  >
                    <div className="flex items-baseline gap-2 mb-5">
                      <span
                        className="font-display font-bold leading-none"
                        style={{ fontSize: 36, letterSpacing: "-0.02em", color: "var(--foreground)" }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        className="font-mono text-[10px] font-bold tracking-[0.2em]"
                        style={{ color: "var(--accent)" }}
                      >
                        /{step.code}
                      </span>
                    </div>
                    <Icon className="h-5 w-5 mb-3 text-muted-foreground" />
                    <h3
                      className="font-display text-[16px] font-bold uppercase mb-2"
                      style={{ letterSpacing: "0.04em" }}
                    >
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
                    <div className="absolute top-3 right-3 font-mono text-[9px] tracking-[0.16em] text-muted-foreground">
                      PASSO {String(i + 1).padStart(2, "0")}/0{arr.length}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Fluxo preview — compact single-row on lg, stacked on smaller screens */}
            <div
              className="mt-6 px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-3"
              style={{ background: "var(--card)", border: "1px solid var(--border)" }}
            >
              <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
                Fluxo · Barris (50 L)
              </span>
              <div className="flex flex-wrap items-center gap-1.5">
                {[
                  { label: t("home.features.stages.stagePipelineClean"),     color: "#2C7A4D" },
                  { label: t("home.features.stages.stagePipelineFilled"),    color: "#14181B" },
                  { label: t("home.features.stages.stagePipelineDelivered"), color: "#E8541C" },
                  { label: t("home.features.stages.stagePipelineReturned"),  color: "#5C5048" },
                ].map((stage, i, arr) => (
                  <div key={stage.label} className="flex items-center gap-1.5">
                    <StagePill label={stage.label} color={stage.color} />
                    {i < arr.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60" />}
                  </div>
                ))}
              </div>
              <span className="ml-auto font-mono text-[10.5px] uppercase tracking-[0.14em]">
                FLUXO PADRÃO · 4 ETAPAS
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 03 — Capacidades */}
      <section
        className="py-20 sm:py-24 lg:py-28"
        style={{ background: "var(--card)", borderBottom: "1px solid var(--border)" }}
      >
        <SectionHeader
          index="03"
          label="CAPACIDADES"
          title={t("home.capabilities.title")}
          subtitle={t("home.capabilities.subtitle")}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {capabilities.map((cap, index) => (
            <CapabilityTile key={cap.title} index={index} item={cap} />
          ))}
        </div>
      </section>

      {/* SPEC SHEET stats strip */}
      <section
        className="py-12 sm:py-14"
        style={{
          background: "var(--foreground)",
          color: "var(--background)",
          borderTop: "1px solid var(--accent)",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="font-mono text-[10px] font-bold uppercase mb-5"
            style={{ color: "var(--accent)", letterSpacing: "0.2em" }}
          >
            — FICHA TÉCNICA
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
            {[
              { v: "100%", l: "Pronto para celular",   k: "M-01" },
              { v: "6+",   l: "Tipos de ativo",        k: "T-02" },
              { v: "∞",    l: "Etapas customizáveis",  k: "S-03" },
              { v: "1",    l: "Acesso multi-conta",    k: "U-04" },
            ].map((s, i) => (
              <div
                key={s.k}
                className="relative px-5"
                style={{
                  borderLeft: i > 0 ? "1px solid rgba(237,238,238,0.13)" : "none",
                }}
              >
                <div
                  className="font-mono text-[9px] font-bold uppercase mb-2"
                  style={{ color: "var(--accent)", letterSpacing: "0.16em" }}
                >
                  {s.k}
                </div>
                <div
                  className="font-display font-bold leading-none"
                  style={{
                    fontSize: 56,
                    color: "var(--background)",
                    letterSpacing: "-0.025em",
                  }}
                >
                  {s.v}
                </div>
                <div
                  className="font-mono text-[11px] uppercase mt-2"
                  style={{ color: "rgba(237,238,238,0.7)", letterSpacing: "0.12em" }}
                >
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — 2-col with spec stamp */}
      <section className="relative overflow-hidden py-20 sm:py-24 lg:py-28" style={{ background: "var(--background)" }}>
        <HazardStripe position="absolute-top" />
        <div
          className="max-w-7xl mx-auto grid gap-10 items-center relative z-10 px-4 sm:px-6 lg:px-8"
          style={{ gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)" }}
        >
          <div>
            <div
              className="font-mono text-[11px] font-bold uppercase mb-3.5"
              style={{ color: "var(--accent)", letterSpacing: "0.2em" }}
            >
              — INSTALAÇÃO · 5 MIN
            </div>
            <h2
              className="font-display font-bold uppercase m-0"
              style={{
                fontSize: "clamp(34px, 5vw, 56px)",
                lineHeight: 1.02,
                letterSpacing: "-0.02em",
                color: "var(--foreground)",
              }}
            >
              {t("home.cta.title")}
            </h2>
            <p
              className="mt-4 text-base sm:text-lg leading-relaxed max-w-xl"
              style={{ color: "var(--muted-foreground)" }}
            >
              {t("home.cta.subtitle")}
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              <a
                href="/register"
                className="inline-flex items-center gap-2 font-mono font-bold uppercase px-5 py-3 transition-opacity hover:opacity-90"
                style={{
                  background: "var(--accent)",
                  color: "var(--accent-foreground)",
                  fontSize: 13,
                  letterSpacing: "0.14em",
                }}
              >
                {t("home.cta.button")} →
              </a>
              <a
                href="/login"
                className="inline-flex items-center gap-2 font-mono font-bold uppercase px-5 py-3 transition-colors hover:bg-secondary"
                style={{
                  background: "transparent",
                  color: "var(--foreground)",
                  border: "1px solid var(--foreground)",
                  fontSize: 13,
                  letterSpacing: "0.14em",
                }}
              >
                AGENDAR DEMO
              </a>
            </div>
          </div>

          {/* Spec stamp */}
          <div
            className="relative p-5"
            style={{ border: "2px solid var(--foreground)", background: "var(--card)" }}
          >
            <div
              className="absolute font-mono text-[10px] font-bold uppercase"
              style={{
                top: -10,
                left: 18,
                padding: "0 8px",
                background: "var(--card)",
                color: "var(--foreground)",
                letterSpacing: "0.16em",
              }}
            >
              BIERTRACK · APROVADO PARA OPERAÇÃO
            </div>
            <div className="grid grid-cols-2 gap-3">
              {(() => {
                const rows: [string, string][] = [
                  ["LICENÇA", "SOFTWARE · MENSAL"],
                  ["IDIOMAS", "PT-BR · EN"],
                  ["IMPLANTAÇÃO", "5 MIN · SEM ESPECIALISTA"],
                ]
                return rows.map(([k, v], i) => (
                  <div
                    key={k}
                    className="py-2"
                    style={{
                      borderBottom: i < rows.length - 2 ? "1px dashed var(--border)" : "none",
                    }}
                  >
                    <div
                      className="font-mono text-[9px] uppercase"
                      style={{ color: "var(--muted-foreground)", letterSpacing: "0.16em" }}
                    >
                      {k}
                    </div>
                    <div
                      className="font-mono text-[12px] font-bold mt-0.5"
                      style={{ color: "var(--foreground)" }}
                    >
                      {v}
                    </div>
                  </div>
                ))
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER (graphite) */}
      <footer style={{ background: "var(--foreground)", color: "var(--background)", padding: "32px 16px" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <BrandMark inverted />
          </div>
          <div
            className="font-mono text-[10px] uppercase"
            style={{ color: "rgba(237,238,238,0.55)", letterSpacing: "0.16em" }}
          >
            © {new Date().getFullYear()} BIERTRACK · TODOS OS DIREITOS RESERVADOS
          </div>
        </div>
      </footer>
    </div>
  )
}
